import { useState } from "react";
import { Clock, Info, Play } from "lucide-react";

import {
  ChangesWarning,
  ConfigTooltip,
  EarlyProvisionInfoModal,
  EarlyTerminationBanner,
  isCaseModified,
  ModeToggle,
  TH,
  ViewConfigButton,
} from "./shared";
import { CaseDetailModal } from "./CaseDetailModal";
import type { BenchmarkCase } from "../types";

interface SelectionViewProps {
  cases: BenchmarkCase[];
  selectedCaseIds: Set<number>;
  maxConcurrent: number;
  maxMessages: number;
  shouldTerminateEarly: boolean;
  isCustomMode: boolean;
  onToggleMode: () => void;
  onToggleCase: (id: number) => void;
  onToggleAll: () => void;
  onSetMaxConcurrent: (n: number) => void;
  onSetMaxMessages: (max: number) => void;
  onSetShouldTerminateEarly: (v: boolean) => void;
  onRun: () => void;
  onViewHistory: () => void;
  historyCount: number;
  selectedCount: number;
}

export function SelectionView({
  cases,
  selectedCaseIds,
  maxConcurrent,
  maxMessages,
  shouldTerminateEarly,
  isCustomMode,
  onToggleMode,
  onToggleCase,
  onToggleAll,
  onSetMaxConcurrent,
  onSetMaxMessages,
  onSetShouldTerminateEarly,
  onRun,
  onViewHistory,
  historyCount,
  selectedCount,
}: SelectionViewProps) {
  const [modalCaseId, setModalCaseId] = useState<number | null>(null);
  const [showEarlyProvisionInfo, setShowEarlyProvisionInfo] = useState(false);
  const modalCase = modalCaseId !== null
    ? cases.find((c) => c.id === modalCaseId)
    : undefined;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-brand-foreground">
          Evaluation
        </h1>
        <ModeToggle isCustomMode={isCustomMode} onToggle={onToggleMode} />
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <span className="text-sm text-brand-muted">
            {selectedCount} of {cases.length} cases selected
          </span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-muted whitespace-nowrap flex items-center gap-1">
              Max Concurrent
              <ConfigTooltip text="The number of evaluation cases to run in parallel. Higher values complete faster but use more resources." />
            </label>
            <input
              type="number"
              min={1}
              max={10}
              value={maxConcurrent}
              onChange={(e) =>
                onSetMaxConcurrent(Math.max(1, Math.min(10, Number(e.target.value))))
              }
              className="w-16 px-2 py-1 text-xs rounded-md border border-brand-border bg-brand-card text-brand-foreground text-center"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-muted whitespace-nowrap flex items-center gap-1">
              Max Messages
              <ConfigTooltip text="The maximum number of messages (user + assistant) allowed per evaluation case before it is marked as abandoned." />
            </label>
            <input
              type="number"
              min={2}
              max={200}
              value={maxMessages}
              onChange={(e) =>
                onSetMaxMessages(Math.max(2, Number(e.target.value)))
              }
              className="w-16 px-2 py-1 text-xs rounded-md border border-brand-border bg-brand-card text-brand-foreground text-center"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-muted whitespace-nowrap flex items-center gap-1">
              Early Product Recommendation Provision
              <button
                onClick={() => setShowEarlyProvisionInfo(true)}
                className="inline-flex p-0.5 rounded hover:bg-brand-primary/10 transition-colors"
              >
                <Info className="w-3.5 h-3.5 text-brand-muted/60 hover:text-brand-foreground cursor-pointer" />
              </button>
            </label>
            <select
              value={shouldTerminateEarly ? "yes" : "no"}
              onChange={(e) => onSetShouldTerminateEarly(e.target.value === "yes")}
              className="px-2 py-1 text-xs rounded-md border border-brand-border bg-brand-card text-brand-foreground"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {historyCount > 0 && (
            <button
              onClick={onViewHistory}
              className="flex items-center gap-2 px-4 py-2 border border-brand-border text-brand-foreground rounded-md hover:bg-brand-primary/5 transition-colors"
            >
              <Clock className="w-4 h-4" />
              History ({historyCount})
            </button>
          )}
          <button
            onClick={onRun}
            disabled={selectedCount === 0}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="w-4 h-4" />
            Run Evaluation
          </button>
        </div>
      </div>

      {shouldTerminateEarly && <EarlyTerminationBanner />}

      <div className="overflow-x-auto max-h-[70vh] overflow-y-auto border border-brand-border rounded-lg shadow">
        <table className="min-w-full bg-brand-card">
          <thead className="sticky top-0 z-10">
            <tr>
              <TH>
                <input
                  type="checkbox"
                  checked={selectedCaseIds.size === cases.length}
                  onChange={onToggleAll}
                  className="rounded border-brand-border"
                />
              </TH>
              <TH>#</TH>
              <TH>Situation</TH>
              <TH>Segment</TH>
              <TH>Expected Suggestion</TH>
              <TH>Persona</TH>
              <TH>{""}</TH>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {cases.map((c) => (
              <tr
                key={c.id}
                onClick={() => onToggleCase(c.id)}
                className="hover:bg-brand-primary/5 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedCaseIds.has(c.id)}
                    onChange={() => onToggleCase(c.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-brand-border"
                  />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-brand-foreground">
                  {c.id}
                </td>
                <td className="px-4 py-3 text-sm text-brand-foreground max-w-[200px] truncate">
                  {c.modifiedCase.situation}
                </td>
                <td className="px-4 py-3 text-sm text-brand-muted max-w-[180px] truncate">
                  {c.modifiedCase.segment || "\u2014"}
                </td>
                <td className="px-4 py-3 text-sm text-brand-muted max-w-[200px] truncate">
                  {c.modifiedCase.expectedSuggestion || "None"}
                </td>
                <td className="px-4 py-3 text-sm text-brand-muted max-w-[250px] truncate">
                  {c.modifiedCase.personaDetail}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">
                    {isCaseModified(c) && <ChangesWarning />}
                    <ViewConfigButton onClick={(e) => { e.stopPropagation(); setModalCaseId(c.id); }} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalCase && (
        <CaseDetailModal
          benchmarkCase={modalCase}
          status="pending"
          messageCount={0}
          onClose={() => setModalCaseId(null)}
        />
      )}

      {showEarlyProvisionInfo && (
        <EarlyProvisionInfoModal onClose={() => setShowEarlyProvisionInfo(false)} />
      )}
    </div>
  );
}
